import FingerprintJS from '@fingerprintjs/fingerprintjs';
import gql from 'graphql-tag';
import { defineStore } from 'pinia';
import apolloClient from '@/apollo/client';
import i18n from '@/i18n';
import { useCompanyStore } from '@/stores/company';
import { useFiskalySystemStore } from '@/stores/fiskaly-system';
import { useLocationsStore } from '@/stores/locations';
import type { User } from '@/types';
import { companyFragment } from './_shared/graphql-fragments';

interface State {
  user: User | null;
  visitorId: string | null;
  fiskalyVersion: string | null;
  salonizedVersion: string | null;
}

export const useUserStore = defineStore('user', {
  state: (): State => ({
    user: null,
    visitorId: null,
    fiskalyVersion: null,
    salonizedVersion: null,
  }),
  getters: {
    needsVerification: (state: State): boolean =>
      !!state.user && !state.user.verified,
    hasFeatureFlag:
      (state: State) =>
      (flag: string): boolean =>
        !!state.user?.featureFlags?.[flag],
  },
  actions: {
    async generateFingerprint() {
      const { company } = useCompanyStore();
      if (company.country !== 'fr') {
        return;
      }

      const fp = await FingerprintJS.load();
      const result = await fp.get();
      this.visitorId = result.visitorId;
    },
    async fetchOrCreateSystem() {
      // Snapshot before the request: the X-Public-Location-Id header is resolved
      // when the mutation starts, so the backend scopes the system to THIS
      // location. Reading the store again after the promise settles could key the
      // uuid under a different location if the user switched mid-flight.
      const locationPublicId = useLocationsStore().location?.publicId;

      return apolloClient
        .mutate({
          mutation: gql`
          mutation fetchOrCreateSystem($input: FetchOrCreateSystemInput!) {
            fetchOrCreateSystem(input: $input) {
              errors
              success
              fiskalySystem {
                uuid
                version
                salonizedVersion
              }
            }
          }
        `,
          variables: { input: {} },
        })
        .then(({ data }) => {
          const fiskalySystem = data?.fetchOrCreateSystem?.fiskalySystem;
          this.fiskalyVersion = fiskalySystem?.version;
          this.salonizedVersion = fiskalySystem?.salonizedVersion;

          // The backend is authoritative over which system this browser is —
          // created or recovered via fingerprint alike — so its answer always
          // overwrites the stored uuid for the requested location.
          if (fiskalySystem?.uuid && locationPublicId) {
            useFiskalySystemStore().storeSystemUuid(
              locationPublicId,
              fiskalySystem.uuid,
            );
          }
        });
    },
    getUser() {
      return new Promise<void>((resolve) => {
        apolloClient
          .query({
            query: gql`
              query getCurrentUser {
                currentUser {
                  admin
                  basic
                  company {
                    ...companyAll
                  }
                  calendarIcsUrl
                  email
                  featureFlags
                  feedbacks
                  firstName
                  franchiseAdmin
                  fullName
                  id
                  impersonated
                  intercomHash
                  lastName
                  manager
                  mustSetPassword
                  owner
                  salonizedAdmin
                  treatwellAdmin
                  verified
                  reports
                  settings {
                    custom {
                      language
                      theme
                    }
                    pushNotifications {
                      newAppointmentInDays
                    }
                    notifications {
                      dayReports
                      updates
                      tradeShowDemoUser
                    }
                    security {
                      autoSessionTimeout
                    }
                  }
                  npsFeedbackDue
                  createdAt
                }
              }
              ${companyFragment}
            `,
          })
          .then(({ data: { currentUser } }) => {
            if (currentUser) {
              const { company, ...user } = currentUser;

              this.user = user;

              const { _setCompany } = useCompanyStore();
              _setCompany(company);

              if (!user.verified) {
                i18n.locale.value = 'en';
              }
            }

            resolve();
          });
      });
    },

    updateUser(input: any) {
      if (!this.user) {
        return;
      }

      const mutationVars = {
        ...input,
        id: this.user?.id,
      };

      return new Promise<void>((resolve, reject) => {
        apolloClient
          .mutate({
            mutation: gql`
              mutation updateUser($input: UpdateUserInput!) {
                updateUser(input: $input) {
                  user {
                    id
                    firstName
                    lastName
                    fullName
                    email
                    settings {
                      custom {
                        language
                        theme
                      }
                      pushNotifications {
                        newAppointmentInDays
                      }
                      notifications {
                        dayReports
                        updates
                      }
                    }
                  }
                  errors
                }
              }
            `,
            variables: {
              input: mutationVars,
            },
          })
          .then(
            ({
              data: {
                updateUser: { user, errors },
              },
            }) => {
              if (errors && Object.keys(errors)?.length) {
                reject(Object.keys(errors));
              } else {
                this.user = {
                  ...this.user,
                  ...user,
                };
                resolve();
              }
            },
          );
      });
    },
  },
});
